import { useState, useRef, useEffect, useCallback } from "react";
import { IsMobileContent } from "../../../media-queries/MediaQueries";
import { DISPLAYMODE } from "../SearchToolbar/DisplayMode";

/**
 * Custom hook for managing hover modal state and positioning
 * 
 * Handles:
 * - Hover state management (card and modal)
 * - Automatic positioning with scroll/resize listeners
 * - Cleanup of event listeners
 * - Mobile detection and conditional behavior
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.displayMode - Current display mode (GRIDCOMPACT, LIST, etc.)
 * @param {boolean} options.enableHoverModal - Whether hover modal is enabled
 * @param {HTMLElement} options.externalModalDiv - DOM element for modal portal
 * @param {Function} options.positionModal - Function to position the modal
 * @returns {Object} Hook state and handlers
 */
function useHoverModal({
  displayMode = DISPLAYMODE.GRIDCOMPACT,
  enableHoverModal = false,
  externalModalDiv = null,
  positionModal = null,
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [isModalHovered, setIsModalHovered] = useState(false);
  const cardRef = useRef(null);
  const isMobile = IsMobileContent();

  // Memoized handlers for mouse events
  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  // Position modal and setup event listeners
  useEffect(() => {
    if (!enableHoverModal || !positionModal) return;

    // Position the modal initially
    positionModal(cardRef, externalModalDiv, displayMode, isHovered, isModalHovered);

    // Only add listeners if conditions are met
    if (
      displayMode === DISPLAYMODE.GRIDCOMPACT &&
      externalModalDiv &&
      (isHovered || isModalHovered) &&
      cardRef.current
    ) {
      const handleScrollResize = () =>
        positionModal(cardRef, externalModalDiv, displayMode, isHovered, isModalHovered);

      window.addEventListener("scroll", handleScrollResize, true);
      window.addEventListener("resize", handleScrollResize);

      return () => {
        window.removeEventListener("scroll", handleScrollResize, true);
        window.removeEventListener("resize", handleScrollResize);
      };
    }
  }, [isHovered, isModalHovered, displayMode, externalModalDiv, enableHoverModal, positionModal]);

  // Determine if modal should be shown
  const shouldShowModal =
    enableHoverModal &&
    displayMode === DISPLAYMODE.GRIDCOMPACT &&
    externalModalDiv &&
    (isHovered || isModalHovered);

  // Mouse event props (only if not mobile and hover enabled)
  const mouseEventProps = !isMobile && enableHoverModal
    ? {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
      }
    : {};

  return {
    // State
    isHovered,
    isModalHovered,
    shouldShowModal,
    
    // Refs
    cardRef,
    
    // Handlers
    setIsModalHovered,
    mouseEventProps,
  };
}

export default useHoverModal;
